/*
 * @FileName       : ui.reducer.ts
 * @Version        : 1.0
 * @DateOfCreation : 04/21/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : UI reducer to handle UI events.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

import createReducer from './createReducer';
import { UIAction } from '../actions';
import { UI } from '../constants/actions';
import dataList from '../../constants/dataList';

export const ui = createReducer(
	{},
	{
		[UI.SET_NAV_STATE](state = {}, action: ReturnType<typeof UIAction.setNavState>) {
			return { ...state, navState: action.payload };
		},

		[UI.STORE_CUSTOMER](state = {}, action: ReturnType<typeof UIAction.storeCustomer>) {
			return { ...state, selectedCustomer: action.payload };
		},

		//*********************************************************************************************
		//***** begin states used in searches
		//todo: redundant with one above
		[UI.SET_CUSTOMER](state = {}, action: ReturnType<typeof UIAction.storeCustomer>) {
			return { ...state, selectedCustomer: action.payload };
		},

		[UI.SET_SELECTED_DATE_RANGE](state = {}, action: ReturnType<typeof UIAction.setSelectedDateRange>) {
			return { ...state, selectedDateRange: action.payload };
		},

		[UI.SET_SELECTED_EXPIRY_DATE](state = {}, action: ReturnType<typeof UIAction.setSelectedExpiryDate>) {
			return { ...state, selectedExpiryDate: action.payload };
		},

		[UI.SET_SELECTED_CABINET_TYPE](state = {}, action: ReturnType<typeof UIAction.setSelectedCabinetType>) {
			return { ...state, selectedCabinetType: action.payload };
		},

		[UI.SET_SELECTED_HISTORY_DATE](state = {}, action: ReturnType<typeof UIAction.setSelectedHistoryDate>) {
			return { ...state, selectedHistoryDate: action.payload };
		},

		[UI.SET_SELECTED_TRANSMITTAL_DATE](state = {}, action: ReturnType<typeof UIAction.setSelectedTransmittalDate>) {
			return { ...state, selectedTransmittalDate: action.payload };
		},

		/*
		 *  user has set a new value for a filter
		 * payload tells us which filter and new value
		 * save to the obj state.ui.selectedFilterValues
		 */
		[UI.SET_MORE_FILTER_VALUE](state = {} as any, action: ReturnType<typeof UIAction.setMoreFilterValue>) {
			const newFilterObj = state.selectedFilterValues
				? { ...state.selectedFilterValues }
				: { ...dataList.DefaultFilterVals };
			newFilterObj[action.payload.filterName] = action.payload.newValue;

			return { ...state, selectedFilterValues: newFilterObj };
		},
		//*********************************************************************************************
		//***** end states used in searches

		[UI.SET_GLOBAL_ROWS_PER_PAGE](state = {}, action: ReturnType<typeof UIAction.setGlobalRowsPerPage>) {
			return { ...state, globalRowsPerPage: action.payload };
		},

		[UI.RESET_ALL_FILTERS](state = {}, action: ReturnType<typeof UIAction.resetAllFilters>) {
			return {
				...state,
				selectedFilterValues: {
					//...dataList.DefaultFilterVals, //for whatever reason this doesn't work, so we have to specify each one manually to be safe
					PRODUCT_NAME: '', //when logging dataList.DefaultFilterVals it seems PRODUCT_NAME is being overridden? Had to specifically clear it here for reset to work
					USER_EMAIL: '', //need to do this explicitly or else first letter is preserved
					CABINET_ID: '',
					PRODUCT_ID: '',
					LOT_NUMBER: '',
					ACTION: '',
					REASON: '',
					INVOICED: '',
					SERIAL_NUMBER: '',
					RFID_TAG: '',
					TAG: '',
					CABINET_TYPE: '',
					CABINET_STATE: '',
					ORDER_STATUS: '',
					SHIPMENT_STATUS: '',
					WAREHOUSE: '',
					INCIDENT_STATUS: '',
					INCIDENT_SEVERITY: '',
					TELEMETRY_ALERT_LEVEL: '',
					USER_FIRST_NAME: '',
					USER_LAST_NAME: '',
					CUSTOMER_SETUP_STATUS: '',
					CUSTOMER_STATE: '',
					CUSTOMER_ZIP_CODE: '',
					CUSTOMER_CREATED_DATE: '',
					SALES_TYPE: '',
					DOSE_TYPE: '',
					TRANSACTION_STATUS: '',
					ORDER_TYPE: '',
				},
				selectedCustomer: null,
				selectedDateRange: null,
				selectedExpiryDate: '',
				selectedCabinetType: '',
				selectedHistoryDate: null,
				selectedResource: null,
				selectedInitiatedBy: null,
				selectedTransmittalDate: null
			};
		},

		[UI.NAVIGATE_RESPONSE](state = {}, action: ReturnType<typeof UIAction.navigationStateSuccess>) {
			return { ...state, menuState: action.payload };
		},

		[UI.CLOUD_MGNT_LINK_LOADING](state = {}, action: ReturnType<typeof UIAction.cloudManagementLinkLoading>) {
			return { ...state, cloudManagementLinkLoading: action.payload };
		},

		[UI.SHOW_LOADER](state = {}, action: ReturnType<typeof UIAction.showLoader>) {
			return { ...state, isLoading: action.payload };
		},

		[UI.AUTH_PROCESSING](state = {}, action: ReturnType<typeof UIAction.setAuthProcessing>) {
			return { ...state, authProcessing: action.payload };
		},

		[UI.GET_INVENTORY_STATE](state = {}, action: ReturnType<typeof UIAction.setInventoryReportFilterStateSuccess>) {
			return { ...state, filterState: action.payload };
		},

		[UI.SET_API_ERROR](state = {}, action: ReturnType<typeof UIAction.setApiError>) {
			return { ...state, errorMessage: action.payload };
		},

		[UI.SET_NAV_DRAWER](state = {}, action: ReturnType<typeof UIAction.setNavDrawer>) {
			return { ...state, navDrawerOpen: action.payload };
		},

		[UI.SET_URL_FILTER](state = {}, action: ReturnType<typeof UIAction.setUrlFilter>) {
			const queryString = action.payload ? action.payload.replace('?', '') : null;
			const params = new URLSearchParams(queryString);
			if (params.get('filter')) {
				const filterString: any = params.get('filter');
				let filter = null;

				try {
					let c = filterString.replace(/[\u200B-\u200D\uFEFF]/g, ''); //replace zero width spaces if present!!
					filter = JSON.parse(decodeURI(c));
				} catch (e) {
					console.error('Error parsing filter json from url:', e);
				}

				return { ...state, urlFilter: filter };
			}

			return { ...state, urlFilter: null };
		},

		[UI.SET_SELECTED_SAVE_FILTER](state = {}, action: ReturnType<typeof UIAction.setNavDrawer>) {
			return { ...state, selectedSavedFilter: action.payload };
		},

		[UI.DISABLE_SERIAL_NUMBER_FILTER](state = {}, action: ReturnType<typeof UIAction.disableSerialNumberFilter>) {
			return { ...state, disableSerialNumberFilter: action.payload };
		},

		[UI.DISABLE_OWNERSHIP_FILTER](state = {}, action: ReturnType<typeof UIAction.disableOwnershipFilter>) {
			return { ...state, disableOwnershipFilter: action.payload };
		},
	},
);
